/**
 * Recording / transcription consent shown when a callee accepts an incoming call.
 */

export type RecordingConsentRole = {
  roleType?: string | null
}

export type RecordingConsentContext = {
  /** True when either party is a provider — recording is mandatory to join. */
  isProviderCall: boolean
  /** Members may opt out of recording (ends the call instead of joining). */
  canOptOut: boolean
  title: string
  body: string
  acceptLabel: string
  declineLabel: string
}

export function isProviderRole(profile: RecordingConsentRole | null | undefined): boolean {
  return (profile?.roleType ?? '').toLowerCase().includes('provider')
}

export function isMemberRole(profile: RecordingConsentRole | null | undefined): boolean {
  return (profile?.roleType ?? '').toLowerCase().includes('member')
}

export function isProviderCall(
  localUser: RecordingConsentRole,
  remoteUser?: RecordingConsentRole | null,
): boolean {
  return isProviderRole(localUser) || isProviderRole(remoteUser)
}

/**
 * Whether the incoming callee must see the recording consent modal before joining.
 * Provider-to-provider calls skip consent; all other 1:1 combinations require it.
 */
export function requiresRecordingConsent(
  localUser: RecordingConsentRole,
  remoteUser?: RecordingConsentRole | null,
): boolean {
  if (isProviderRole(localUser) && isProviderRole(remoteUser)) return false
  return true
}

export function isIndependentRole(profile: RecordingConsentRole | null | undefined): boolean {
  return (profile?.roleType ?? '').toLowerCase().includes('independent')
}

/**
 * Members and Independents (not providers) may decline recording — the call
 * ends instead of joining, and the refusal is recorded as their durable
 * recording-policy decision (declined = blocked both directions until they
 * accept). Providers can never opt out.
 */
export function canMemberOptOutOfRecording(localUser: RecordingConsentRole): boolean {
  if (isProviderRole(localUser)) return false
  return isMemberRole(localUser) || isIndependentRole(localUser)
}

/**
 * Which presentation of the notice the user is seeing:
 * - 'pre-join': the modal between tapping Accept and joining (the default).
 * - 'post-connect': the in-call sheet shown when the user answered somewhere no
 *   notice could be presented (iOS CallKit lock-screen answer) and joined with
 *   the notice still owed. Same notice body — only the action labels adapt to
 *   the call already being live, so the stored NOTICE_VERSION stays valid.
 */
export type RecordingConsentNoticeVariant = 'pre-join' | 'post-connect'

export function buildRecordingConsentContext(
  localUser: RecordingConsentRole,
  remoteUser?: RecordingConsentRole | null,
  mediaType: 'voice' | 'video' = 'voice',
  variant: RecordingConsentNoticeVariant = 'pre-join',
): RecordingConsentContext {
  const providerCall = isProviderCall(localUser, remoteUser)
  const canOptOut = canMemberOptOutOfRecording(localUser)
  const callKind = mediaType === 'video' ? 'Video' : 'Voice'

  const mandatoryNote = providerCall
    ? ' Calls with a care provider are always recorded and transcribed for quality and documentation.'
    : ''

  const body = `${callKind} calls in Ascend may be recorded and transcribed for care coordination, quality assurance, and your records.${mandatoryNote} To join, you must consent to recording.`

  if (variant === 'post-connect') {
    return {
      isProviderCall: providerCall,
      canOptOut,
      title: 'Recording & transcription notice',
      body,
      acceptLabel: 'I consent — continue call',
      declineLabel: canOptOut ? 'Opt out and end call' : 'Decline and leave call',
    }
  }

  return {
    isProviderCall: providerCall,
    canOptOut,
    title: 'Recording & transcription notice',
    body,
    acceptLabel: 'I consent — join call',
    declineLabel: canOptOut ? 'Opt out and end call' : 'Decline call',
  }
}

/**
 * Identifies WHICH notice a consent record refers to. Bump whenever the copy in
 * `buildRecordingConsentContext` changes materially, so a stored consent record
 * can be traced back to what the user was actually shown.
 */
export const RECORDING_CONSENT_NOTICE_VERSION = 'recording-notice-2026-07'

export type RecordingConsentAcceptPayload = {
  recordingConsent: true
  recordingConsentAt: string
  recordingConsentNoticeVersion: string
}

export function buildRecordingConsentAcceptPayload(
  at: Date = new Date(),
): RecordingConsentAcceptPayload {
  return {
    recordingConsent: true,
    recordingConsentAt: at.toISOString(),
    recordingConsentNoticeVersion: RECORDING_CONSENT_NOTICE_VERSION,
  }
}

/** Why no consent notice was shown before the call was joined. */
export type RecordingConsentSkipReason =
  /** No UI could be presented — iOS CallKit answer on a locked device. */
  | 'NO_NOTICE_UI'
  /** Policy says the notice is not required (provider-to-provider call). */
  | 'NOT_REQUIRED'
  /**
   * The answering party is provider staff and answered where no notice could
   * be shown (Android Auto / Bluetooth / lock screen / CallKit). Recording of
   * provider calls is mandatory under org policy and providers can never opt
   * out, so nothing is owed on THEIR side. The other party's consent is
   * separately verified server-side (durable recording-policy acceptance)
   * before capture is allowed to start.
   */
  | 'PROVIDER_POLICY'

/**
 * Skip reason for an answer that happened where no notice UI could be shown.
 * Provider-to-provider owes nothing; a provider answerer is covered by org
 * policy; anyone else still owes the notice (post-connect sheet).
 */
export function noticeSkipReasonForUnnoticedAnswer(
  localUser: RecordingConsentRole,
  remoteUser?: RecordingConsentRole | null,
): RecordingConsentSkipReason {
  if (!requiresRecordingConsent(localUser, remoteUser)) return 'NOT_REQUIRED'
  if (isProviderRole(localUser)) return 'PROVIDER_POLICY'
  return 'NO_NOTICE_UI'
}

/** Skip reasons that resolve the capture gate without an affirmative consent. */
export function isCaptureAllowingSkipReason(reason: unknown): boolean {
  return reason === 'NOT_REQUIRED' || reason === 'PROVIDER_POLICY'
}

/**
 * Marker sent when the accept happened WITHOUT the consent notice being shown.
 * It asserts nothing about the user's consent — it only records that the notice
 * is still owed, so the call can be identified later for the post-connect
 * prompt and for compliance reporting.
 */
export type RecordingConsentBypassedPayload = {
  recordingConsentBypassed: true
  recordingConsentBypassedAt: string
  recordingConsentSkipReason: RecordingConsentSkipReason
}

export function buildRecordingConsentBypassedPayload(
  skipReason: RecordingConsentSkipReason = 'NO_NOTICE_UI',
  at: Date = new Date(),
): RecordingConsentBypassedPayload {
  return {
    recordingConsentBypassed: true,
    recordingConsentBypassedAt: at.toISOString(),
    recordingConsentSkipReason: skipReason,
  }
}

export type RecordingConsentAcceptContext = {
  /** True ONLY when the notice was displayed and the user confirmed it. */
  noticeConfirmed: boolean
  /** Why the notice was not shown. Ignored when `noticeConfirmed` is true. */
  noticeSkipReason?: RecordingConsentSkipReason
}

/**
 * Payload for an ACCEPT signal. An affirmative consent claim is only ever sent
 * when the notice was actually presented to and confirmed by the user. Every
 * other accept — the iOS CallKit bypass, and provider-to-provider calls where
 * the notice is not shown at all — sends the bypassed marker, so no consent
 * record is created for a notice nobody saw.
 */
export function buildRecordingConsentSignalPayload(
  context: RecordingConsentAcceptContext,
  at: Date = new Date(),
): RecordingConsentAcceptPayload | RecordingConsentBypassedPayload {
  return context.noticeConfirmed
    ? buildRecordingConsentAcceptPayload(at)
    : buildRecordingConsentBypassedPayload(context.noticeSkipReason ?? 'NO_NOTICE_UI', at)
}

/**
 * Whether an answer that joined WITHOUT the notice (bypassed accept) owes the
 * user the POST-CONNECT consent sheet. This is the consumer of the
 * `recordingConsentBypassed` ("notice owed") marker: true means the in-call UI
 * must present the notice the moment the call connects, and recording consent
 * is resolved only by that sheet. Mirrors the pre-join gate exactly —
 * durable acceptance or a provider-to-provider call owes nothing.
 */
export function owesPostConnectConsentNotice(input: {
  /** True when the accept skipped the notice UI (iOS CallKit lock-screen answer). */
  bypassedNotice: boolean
  /** True when this user already holds a durable recording-policy ACCEPTED. */
  hasDurableAcceptance: boolean
  localUser: RecordingConsentRole
  remoteUser?: RecordingConsentRole | null
}): boolean {
  if (!input.bypassedNotice) return false
  if (input.hasDurableAcceptance) return false
  return noticeSkipReasonForUnnoticedAnswer(input.localUser, input.remoteUser) === 'NO_NOTICE_UI'
}

/**
 * CONSENT-signal payload for the post-connect sheet's confirm button: the same
 * affirmative consent record a modal-confirmed ACCEPT carries, plus a marker
 * that the notice was presented IN-CALL (after a lock-screen answer) rather
 * than pre-join — so compliance reporting can distinguish the two.
 */
export type PostConnectRecordingConsentPayload = RecordingConsentAcceptPayload & {
  recordingConsentPostConnect: true
}

export function buildPostConnectRecordingConsentAcceptPayload(
  at: Date = new Date(),
): PostConnectRecordingConsentPayload {
  return {
    ...buildRecordingConsentAcceptPayload(at),
    recordingConsentPostConnect: true,
  }
}

/**
 * Server-side capture gate: whether the live session row's consent state allows
 * media capture (recording + live transcription) to START for a 1:1 call.
 *
 * - An affirmative consent (modal-confirmed, post-connect-confirmed, or durable
 *   prior acceptance) → yes.
 * - `NOT_REQUIRED` (provider-to-provider — recording is mandatory, no consent
 *   is owed) → yes.
 * - `PROVIDER_POLICY` (provider answerer, org policy) → yes. The signal lambda
 *   only ever stores it after verifying the other party's durable acceptance.
 * - A still-owed notice (`NO_NOTICE_UI`) or no decision yet → NO. Capture stays
 *   deferred until a consent-carrying signal arrives.
 * - The 30-second consent window lapsed (`consentTimedOutAt`) → NO, for the
 *   rest of the call.
 */
export function recordingCaptureAllowedByConsent(
  state:
    | Pick<
        RecordingConsentSessionState,
        | 'recordingConsentAccepted'
        | 'recordingConsentStatus'
        | 'recordingConsentSkipReason'
        | 'consentTimedOutAt'
      >
    | null
    | undefined,
): boolean {
  if (!state) return false
  if (isCallNotRecordedForConsent(state)) return false
  if (state.recordingConsentAccepted === true) return true
  const status = (state.recordingConsentStatus ?? '').toUpperCase()
  if (status === 'ACCEPTED') return true
  if (status === 'PENDING' && isCaptureAllowingSkipReason(state.recordingConsentSkipReason)) {
    return true
  }
  return false
}

/**
 * Whether a consent-carrying signal payload (ACCEPT or CONSENT) resolves the
 * capture gate — the signal-lambda trigger for a DEFERRED capture start.
 * Deliberately parallel to `recordingCaptureAllowedByConsent`, but reading the
 * in-flight payload instead of the stored session state.
 */
export function signalPayloadAllowsCaptureStart(
  payload: Record<string, unknown> | null | undefined,
): boolean {
  if (!payload) return false
  if (payload.recordingConsent === true) return true
  if (
    payload.recordingConsentBypassed === true &&
    isCaptureAllowingSkipReason(payload.recordingConsentSkipReason)
  ) {
    return true
  }
  return false
}

export type RecordingConsentOptOutPayload = {
  reason: 'RECORDING_OPT_OUT'
}

export function buildRecordingConsentOptOutPayload(): RecordingConsentOptOutPayload {
  return { reason: 'RECORDING_OPT_OUT' }
}

// --- Durable consent record (call history) --------------------------------

/**
 * Consent state as stored on the LIVE call session row (`SESSION#<callId>`),
 * which only survives the call itself.
 */
export type RecordingConsentSessionState = {
  recordingConsentAccepted?: boolean | null
  recordingConsentStatus?: string | null
  recordingConsentAt?: string | null
  recordingConsentBypassedAt?: string | null
  recordingConsentSkipReason?: string | null
  recordingConsentNoticeVersion?: string | null
  /**
   * Set once the 30-second consent window lapsed: THIS call is not recorded for
   * the rest of its life, whatever consent arrives later.
   */
  consentTimedOutAt?: string | null
}

export type RecordingConsentStatus = 'ACCEPTED' | 'PENDING' | 'DECLINED' | 'UNKNOWN'

export type RecordingConsentSource =
  /** The notice was displayed and the user confirmed it. */
  | 'MODAL_CONFIRMED'
  /** Answered where no notice could be presented (iOS CallKit / locked device). */
  | 'NO_NOTICE_SHOWN'
  /** Policy skipped the notice (provider-to-provider call). */
  | 'NOTICE_NOT_REQUIRED'
  /** Provider answerer with no notice UI; recording covered by org policy. */
  | 'PROVIDER_POLICY'
  /** The user explicitly refused recording, which ended the call. */
  | 'RECORDING_OPT_OUT'
  /** No live session row existed at terminal time (swept, or never created). */
  | 'NO_SESSION_RECORD'
  /** A session row existed but carried no consent decision (e.g. never answered). */
  | 'NO_CONSENT_RECORDED'

/**
 * The consent facts written onto the DURABLE call-history record. All fields are
 * additive; `UNKNOWN` is always preferred over an unearned `ACCEPTED`.
 */
export type DurableRecordingConsent = {
  recordingConsentStatus: RecordingConsentStatus
  recordingConsentSource: RecordingConsentSource
  /** When the decision was made, or when the missing notice was noted. */
  recordingConsentAt?: string
  /** The user the decision (or omission) belongs to — the answering party. */
  recordingConsentBy?: string
  /** Which notice copy/policy version the user was shown, when known. */
  recordingConsentNoticeVersion?: string
}

function consentSourceForSkipReason(reason: unknown): RecordingConsentSource {
  if (reason === 'NOT_REQUIRED') return 'NOTICE_NOT_REQUIRED'
  if (reason === 'PROVIDER_POLICY') return 'PROVIDER_POLICY'
  return 'NO_NOTICE_SHOWN'
}

/**
 * Derive the durable consent record for a finished call.
 *
 * Consent is only ever captured from the ANSWERING party (the callee); the
 * caller is never shown a notice, so a caller's own consent is never claimed
 * here — both parties' history records describe the callee's decision, and
 * `recordingConsentBy` names whose decision it is.
 */
export function resolveDurableRecordingConsent(input: {
  /** Live session row consent state, or null when no session row remained. */
  session: RecordingConsentSessionState | null | undefined
  /** Uppercased terminal reason (e.g. `RECORDING_OPT_OUT`). */
  terminalReason?: string | null
  /** The answering party — whose consent this record describes. */
  calleeId?: string | null
  /** Fallback timestamp (terminal time) when no consent timestamp exists. */
  at: string
}): DurableRecordingConsent {
  const consentedBy = input.calleeId ? { recordingConsentBy: input.calleeId } : {}

  if ((input.terminalReason ?? '').toUpperCase() === 'RECORDING_OPT_OUT') {
    return {
      recordingConsentStatus: 'DECLINED',
      recordingConsentSource: 'RECORDING_OPT_OUT',
      recordingConsentAt: input.at,
      ...consentedBy,
    }
  }

  const session = input.session
  if (!session) {
    return {
      recordingConsentStatus: 'UNKNOWN',
      recordingConsentSource: 'NO_SESSION_RECORD',
    }
  }

  if (session.recordingConsentAccepted === true || session.recordingConsentStatus === 'ACCEPTED') {
    return {
      recordingConsentStatus: 'ACCEPTED',
      recordingConsentSource: 'MODAL_CONFIRMED',
      recordingConsentAt: session.recordingConsentAt ?? input.at,
      ...consentedBy,
      ...(session.recordingConsentNoticeVersion
        ? { recordingConsentNoticeVersion: session.recordingConsentNoticeVersion }
        : {}),
    }
  }

  if (session.recordingConsentStatus === 'PENDING') {
    return {
      recordingConsentStatus: 'PENDING',
      recordingConsentSource: consentSourceForSkipReason(session.recordingConsentSkipReason),
      recordingConsentAt: session.recordingConsentBypassedAt ?? input.at,
      ...consentedBy,
    }
  }

  return {
    recordingConsentStatus: 'UNKNOWN',
    recordingConsentSource: 'NO_CONSENT_RECORDED',
  }
}

// --- Per-attendee consent records -----------------------------------------

/**
 * Consent is a decision by an INDIVIDUAL, not by a call: a group call has one
 * decision per attendee. Each attendee's decision is stored on its own durable
 * row, and this derives it from the ACCEPT/REJECT signal that attendee sent.
 *
 * `null` means the signal said nothing about consent, so nothing is recorded —
 * absence reads as UNKNOWN, never as accepted.
 */
export function resolveAttendeeRecordingConsent(input: {
  userId: string
  /** Parsed signal payload from that attendee's ACCEPT (or terminal REJECT). */
  payload: Record<string, unknown> | null | undefined
  /** Fallback timestamp (signal time). */
  at: string
}): DurableRecordingConsent | null {
  const payload = input.payload
  const by = { recordingConsentBy: input.userId }

  if ((typeof payload?.reason === 'string' ? payload.reason : '').toUpperCase() === 'RECORDING_OPT_OUT') {
    return {
      recordingConsentStatus: 'DECLINED',
      recordingConsentSource: 'RECORDING_OPT_OUT',
      recordingConsentAt: input.at,
      ...by,
    }
  }

  if (payload?.recordingConsent === true) {
    return {
      recordingConsentStatus: 'ACCEPTED',
      recordingConsentSource: 'MODAL_CONFIRMED',
      recordingConsentAt:
        typeof payload.recordingConsentAt === 'string' ? payload.recordingConsentAt : input.at,
      ...by,
      ...(typeof payload.recordingConsentNoticeVersion === 'string'
        ? { recordingConsentNoticeVersion: payload.recordingConsentNoticeVersion }
        : {}),
    }
  }

  if (payload?.recordingConsentBypassed === true) {
    return {
      recordingConsentStatus: 'PENDING',
      recordingConsentSource: consentSourceForSkipReason(payload.recordingConsentSkipReason),
      recordingConsentAt:
        typeof payload.recordingConsentBypassedAt === 'string'
          ? payload.recordingConsentBypassedAt
          : input.at,
      ...by,
    }
  }

  return null
}

/**
 * Consent facts for ONE call-history record. A record always describes the user
 * named in `recordingConsentBy`: their own decision when they made one (this is
 * what group calls need — one decision per attendee), otherwise the call-level
 * fallback (for a 1:1 caller, who is never asked, that is the callee's decision,
 * matching the per-call behaviour this builds on).
 */
export function consentForHistoryRecord(
  userId: string,
  byUser: Record<string, DurableRecordingConsent> | null | undefined,
  callLevel?: DurableRecordingConsent | null,
): DurableRecordingConsent | undefined {
  return byUser?.[userId] ?? callLevel ?? undefined
}

export type RecordingConsentArtifactSummary = {
  recordingConsentStatus: RecordingConsentStatus
  /** Attendees who affirmatively consented (may be empty). */
  recordingConsentAcceptedBy: string[]
}

// --- Durable per-USER recording-policy consent ------------------------------
//
// One decision per USER (not per call): a Member/Independent accepts the
// recording policy ONCE — on their first call in either direction, or from
// Settings — and that acceptance covers making AND receiving calls from then
// on. A durable decline blocks calling in both directions (enforced by the
// publish-voice-call-signal INVITE gate) until they accept.
//
// Stored on the Users-table row, stamped server-side by the update-me lambda.

export type RecordingPolicyConsentStatus = 'ACCEPTED' | 'DECLINED'

/** The Users-row attributes carrying the durable recording-policy decision. */
export type RecordingPolicyConsentFields = {
  recordingPolicyConsentStatus?: string | null
  recordingPolicyConsentAt?: string | null
  recordingPolicyConsentNoticeVersion?: string | null
  /** Which wording the decision was made from (absent on older decisions = original). */
  recordingPolicyConsentWording?: string | null
  /** Where the decision was made (`RecordingPolicyConsentContext`; absent from older builds). */
  recordingPolicyConsentContext?: string | null
}

/**
 * Whether this user must accept the recording policy before calling.
 * Members and Independents: yes. Provider staff: never (recording of their
 * calls is mandatory and not consent-gated on their side).
 */
export function requiresRecordingPolicyAcceptance(
  user: RecordingConsentRole | null | undefined,
): boolean {
  if (!user) return false
  if (isProviderRole(user)) return false
  return isMemberRole(user) || isIndependentRole(user)
}

/**
 * The version of the durable recording POLICY an account accepts. Acceptance is
 * stored server-side on the account (Users row) stamped with this value, and a
 * user is re-asked ONLY when it changes — never because of an app update,
 * reinstall, new device, or cleared local storage. Bump it only when the policy
 * itself changes materially.
 */
export const RECORDING_POLICY_VERSION = 'recording-notice-2026-07'

/**
 * The same policy in Easy Read wording (first-run setup, pre-dial gate and
 * Settings from Android 134 / iOS 99). Recorded in the separate
 * `recordingPolicyConsentWording` attribute: `recordingPolicyConsentNoticeVersion`
 * keeps `RECORDING_POLICY_VERSION`, because Android ≤ 133 / iOS ≤ 98 compare
 * it exactly and would re-ask anyone stamped with another string.
 */
export const RECORDING_POLICY_EASY_READ_VERSION = 'recording-easyread-2026-11'

/**
 * Wordings of ONE policy: an acceptance stamped with any of them is current,
 * so moving to Easy Read blocks nobody's calls. Add a wording here only when
 * the policy itself is unchanged; a real policy change gets a new, separate
 * version.
 */
export const RECORDING_POLICY_EQUIVALENT_VERSIONS: readonly string[] = [
  RECORDING_POLICY_VERSION,
  RECORDING_POLICY_EASY_READ_VERSION,
]

/**
 * Where a durable decision was made (new clients send it with the decision;
 * Android ≤ 133 / iOS ≤ 98 never do).
 */
export const RECORDING_POLICY_CONSENT_CONTEXTS = [
  'setup',
  'resend',
  'call',
  'settings',
  'in-call',
] as const
export type RecordingPolicyConsentContext = (typeof RECORDING_POLICY_CONSENT_CONTEXTS)[number]

export function normalizeRecordingPolicyConsentContext(
  raw: unknown,
): RecordingPolicyConsentContext | null {
  return typeof raw === 'string' &&
    (RECORDING_POLICY_CONSENT_CONTEXTS as readonly string[]).includes(raw)
    ? (raw as RecordingPolicyConsentContext)
    : null
}

/**
 * Where new builds show the Easy Read wording. The in-call notice (pre-join
 * modal / post-connect sheet) keeps the original copy, so `in-call` records
 * the original wording.
 */
const EASY_READ_CONTEXTS: ReadonlySet<RecordingPolicyConsentContext> = new Set([
  'setup',
  'resend',
  'call',
  'settings',
])

/**
 * The wording a decision was made from (`recordingPolicyConsentWording`). A
 * decision without a context came from an older build that showed the
 * original notice.
 */
export function recordingPolicyWordingForDecision(
  context: RecordingPolicyConsentContext | null | undefined,
): string {
  return context && EASY_READ_CONTEXTS.has(context)
    ? RECORDING_POLICY_EASY_READ_VERSION
    : RECORDING_POLICY_VERSION
}

/**
 * Acceptances recorded before the version was stamped on the account carry no
 * version; they were given against this policy text.
 */
const RECORDING_POLICY_BASELINE_VERSION = 'recording-notice-2026-07'

export function acceptedRecordingPolicyVersion(
  user: RecordingPolicyConsentFields | null | undefined,
): string | null {
  if ((user?.recordingPolicyConsentStatus ?? '').toUpperCase() !== 'ACCEPTED') return null
  const stamped = (user?.recordingPolicyConsentNoticeVersion ?? '').trim()
  return stamped || RECORDING_POLICY_BASELINE_VERSION
}

function versionsEquivalentTo(version: string): readonly string[] {
  return RECORDING_POLICY_EQUIVALENT_VERSIONS.includes(version)
    ? RECORDING_POLICY_EQUIVALENT_VERSIONS
    : [version]
}

/**
 * Accepted the CURRENT recording policy, in any of its equivalent wordings
 * (an acceptance of an older policy re-asks once).
 */
export function hasAcceptedRecordingPolicy(
  user: RecordingPolicyConsentFields | null | undefined,
  currentVersion: string = RECORDING_POLICY_VERSION,
): boolean {
  const accepted = acceptedRecordingPolicyVersion(user)
  return accepted !== null && versionsEquivalentTo(currentVersion).includes(accepted)
}

export function hasDeclinedRecordingPolicy(
  user: RecordingPolicyConsentFields | null | undefined,
): boolean {
  return (user?.recordingPolicyConsentStatus ?? '').toUpperCase() === 'DECLINED'
}

/** True when the user has made NO durable recording-policy decision yet. */
export function hasUndecidedRecordingPolicy(
  user: RecordingPolicyConsentFields | null | undefined,
): boolean {
  return !hasAcceptedRecordingPolicy(user) && !hasDeclinedRecordingPolicy(user)
}

/**
 * Message shown to a user whose OWN durable decline blocks the call. Shared by
 * the server INVITE gate and the frontend pre-dial gate so it is never silent.
 */
export const RECORDING_POLICY_DECLINED_SELF_MESSAGE =
  "You haven't accepted the call recording policy. All calls are recorded — accept to enable calling."

/** Message shown to a caller whose target durably declined the policy. */
export function recordingPolicyDeclinedPeerMessage(displayName?: string | null): string {
  const name = (displayName ?? '').trim() || 'This person'
  return `${name} can't receive calls until they accept the call recording policy.`
}

/**
 * Distinct, human-readable rejection for a CAR-originated INVITE from a caller
 * whose recording-policy consent is still UNDECIDED (#342). The CarPlay /
 * Android Auto templates cannot present the recording notice, so the server
 * blocks the call until the user decides on their phone. The exact string is
 * the error contract: the client detects this rejection by message and the car
 * screen should surface it as an alert template.
 */
export const RECORDING_POLICY_CAR_UNDECIDED_MESSAGE =
  'Open Ascend Messenger on your phone to accept the recording policy before calling.'

/**
 * True when a voice-call signal payload marks the call as CAR-originated
 * (CarPlay / Android Auto template tap-to-call, #337). Set by the client's
 * car dial path; consumed by the server INVITE gate.
 */
export function isCarOriginatedSignalPayload(
  payload: Record<string, unknown> | null | undefined,
): boolean {
  return payload?.fromCar === true
}

/**
 * Server-side INVITE gate: why (if at all) this 1:1 call must be blocked on
 * durable recording-policy grounds. Returns a user-facing message, or null when
 * the call may proceed.
 *
 * - An explicit durable DECLINED blocks in both directions, always.
 * - A caller who has never decided is let through for PHONE-originated calls
 *   (the client presents and captures the notice before dialing / answering),
 *   but is BLOCKED for CAR-originated calls (#342): the car templates skip the
 *   client consent gate and can show no notice, so without this check a user
 *   seeded into car recents by an incoming ring could place a recorded call
 *   without ever seeing the recording-consent notice.
 */
export function recordingPolicyCallBlockReason(
  caller: (RecordingConsentRole & RecordingPolicyConsentFields) | null | undefined,
  callee: (RecordingConsentRole & RecordingPolicyConsentFields & { displayName?: string | null }) | null | undefined,
  options?: { fromCar?: boolean },
): string | null {
  if (caller && requiresRecordingPolicyAcceptance(caller) && hasDeclinedRecordingPolicy(caller)) {
    return RECORDING_POLICY_DECLINED_SELF_MESSAGE
  }
  if (
    options?.fromCar &&
    caller &&
    requiresRecordingPolicyAcceptance(caller) &&
    hasUndecidedRecordingPolicy(caller)
  ) {
    return RECORDING_POLICY_CAR_UNDECIDED_MESSAGE
  }
  if (callee && requiresRecordingPolicyAcceptance(callee) && hasDeclinedRecordingPolicy(callee)) {
    return recordingPolicyDeclinedPeerMessage(callee.displayName)
  }
  return null
}

/**
 * Copy for the durable recording-policy notice (pre-dial gate + Settings).
 * Kept in the same tone as the per-call notice in `buildRecordingConsentContext`
 * — one policy, one voice.
 */
export type RecordingPolicyNoticeContext = {
  title: string
  body: string
  acceptLabel: string
  declineLabel: string
}

export function buildRecordingPolicyNoticeContext(): RecordingPolicyNoticeContext {
  return {
    title: 'Recording & transcription notice',
    body:
      'Voice and video calls in Ascend may be recorded and transcribed for care ' +
      'coordination, quality assurance, and your records. Calls with a care provider ' +
      'are always recorded and transcribed for quality and documentation. To make or ' +
      'receive calls, you must consent to recording.',
    acceptLabel: 'I consent — enable calling',
    declineLabel: "Don't allow recording",
  }
}

/**
 * The Easy Read wording (`RECORDING_POLICY_EASY_READ_VERSION`). Decisions made
 * from it record that wording, so change this copy only together with a new
 * wording id. Needs owner/legal sign-off before release.
 */
export const RECORDING_POLICY_EASY_READ_COPY = {
  consent: {
    title: 'Calls are recorded',
    lines: [
      'Ascend records voice and video calls.',
      'A computer writes down what is said.',
      'This helps your support team keep good notes.',
      'Calls with your care team are always recorded.',
      'The recording is kept safely with your care records.',
      'To make or get calls, you need to say yes.',
    ],
    question: 'Do you agree to calls being recorded?',
    acceptLabel: 'Yes, I agree',
    declineLabel: 'No',
    laterLabel: 'Decide later',
    declineConfirm: {
      title: 'Are you sure?',
      lines: [
        "If you say no, you can't make or get calls.",
        'You can change your answer later in Menu → Call recording.',
      ],
      confirmLabel: "Yes, I'm sure",
      cancelLabel: 'Go back',
    },
    afterAccept: 'Thank you. Your calls will work.',
    afterDecline: 'OK. Calls are turned off for you. You can change this in Menu.',
    resendLine: 'Your organisation asked you to answer this again.',
  },
  acknowledge: {
    title: 'Calls are recorded',
    lines: [
      'Calls in Ascend are recorded.',
      'A computer writes down what is said.',
      'Calls with members are always recorded.',
      'This is for care notes and quality checks.',
    ],
    acceptLabel: 'I understand',
    laterLabel: 'Not now',
    resendLine: 'Your organisation asked you to answer this again.',
  },
} as const

/**
 * ACCEPT-signal payload for an answerer who already holds a durable
 * recording-policy acceptance: the per-call notice is skipped, and the ACCEPT
 * asserts the PRIOR consent (original timestamp + the notice version the user
 * actually confirmed) instead of a bypassed marker — the user WAS asked, just
 * on an earlier call.
 */
export function buildPriorRecordingConsentAcceptPayload(input: {
  acceptedAt: string
  noticeVersion?: string | null
}): RecordingConsentAcceptPayload {
  return {
    recordingConsent: true,
    recordingConsentAt: input.acceptedAt,
    recordingConsentNoticeVersion: input.noticeVersion || RECORDING_CONSENT_NOTICE_VERSION,
  }
}

/**
 * Roll the attendee decisions up for the recording artifact itself, so an audio
 * file can be traced to its consent position without a second lookup. Pessimistic
 * on purpose: any refusal shows as DECLINED, and anything short of unanimous
 * consent is PENDING rather than ACCEPTED.
 */
export function summarizeRecordingConsentForArtifact(
  consents: DurableRecordingConsent[],
): RecordingConsentArtifactSummary {
  const acceptedBy = consents
    .filter((c) => c.recordingConsentStatus === 'ACCEPTED')
    .map((c) => c.recordingConsentBy)
    .filter((id): id is string => Boolean(id))

  if (consents.length === 0) {
    return { recordingConsentStatus: 'UNKNOWN', recordingConsentAcceptedBy: [] }
  }
  if (consents.some((c) => c.recordingConsentStatus === 'DECLINED')) {
    return { recordingConsentStatus: 'DECLINED', recordingConsentAcceptedBy: acceptedBy }
  }
  if (consents.every((c) => c.recordingConsentStatus === 'ACCEPTED')) {
    return { recordingConsentStatus: 'ACCEPTED', recordingConsentAcceptedBy: acceptedBy }
  }
  return { recordingConsentStatus: 'PENDING', recordingConsentAcceptedBy: acceptedBy }
}

/**
 * Server-side check behind a `PROVIDER_POLICY` answer: org policy covers the
 * provider answerer, but the OTHER party must still be covered on their own
 * terms before capture may start — another provider (mandatory recording), or
 * a Member/Independent holding a durable recording-policy ACCEPTED. Unknown
 * roles are not covered.
 */
export function providerPolicyCoversCounterpart(
  counterpart: (RecordingConsentRole & RecordingPolicyConsentFields) | null | undefined,
): boolean {
  if (!counterpart) return false
  if (isProviderRole(counterpart)) return true
  if (requiresRecordingPolicyAcceptance(counterpart)) return hasAcceptedRecordingPolicy(counterpart)
  return false
}

/**
 * Consent for a 1:1 CALLEE whose media joined WITHOUT going through
 * `acceptCall` (backend rehydrate after a native/car answer, or a relaunch
 * mid-call). No notice was shown on this path, so it follows the same rules as
 * any other unnoticed answer: durable acceptance asserts the prior consent,
 * provider-to-provider / provider-policy resolve the gate, anyone else owes the
 * post-connect sheet. Re-sending is safe: the server never downgrades a
 * recorded ACCEPTED.
 */
export function planRejoinedCalleeConsent(input: {
  hasDurableAcceptance: boolean
  durableAcceptedAt?: string | null
  durableNoticeVersion?: string | null
  localUser: RecordingConsentRole
  remoteUser?: RecordingConsentRole | null
  at?: Date
}): {
  payload: RecordingConsentAcceptPayload | RecordingConsentBypassedPayload
  owesPostConnectNotice: boolean
} {
  const at = input.at ?? new Date()
  if (input.hasDurableAcceptance) {
    return {
      payload: buildPriorRecordingConsentAcceptPayload({
        acceptedAt: input.durableAcceptedAt ?? at.toISOString(),
        noticeVersion: input.durableNoticeVersion,
      }),
      owesPostConnectNotice: false,
    }
  }
  const reason = noticeSkipReasonForUnnoticedAnswer(input.localUser, input.remoteUser)
  return {
    payload: buildRecordingConsentBypassedPayload(reason, at),
    owesPostConnectNotice: reason === 'NO_NOTICE_UI',
  }
}

// --- 30-second consent window ---------------------------------------------
//
// A 1:1 call whose recording consent is still owed once it connects (car
// answer, CallKit lock-screen answer, relaunch rejoin, any other path that
// skipped the notice) stays connected for CONSENT_WINDOW_MS with capture
// deferred. Consent inside the window starts recording. Otherwise the call
// goes on UNRECORDED (owner decision D): the server marks THIS call not
// recorded, never starts capture for it, discards anything already captured,
// and tells both parties with a non-blocking notice. The account's durable
// decision is untouched, so the member is asked again on their next call.

export const CONSENT_WINDOW_MS = 30_000

/**
 * END reason older servers used when the window ended the call. Nothing sends
 * it any more; clients still honour it, and the server refuses it from clients.
 */
export const RECORDING_CONSENT_MISSING_END_REASON = 'RECORDING_CONSENT_MISSING'

/** Why a call is not recorded, on its SESSION row and in the notice payload. */
export const CALL_NOT_RECORDED_CONSENT_MISSING = 'RECORDING_CONSENT_MISSING'

/**
 * `senderId` / `senderDeviceId` of server-originated consent-window signals.
 * Cognito callers always publish as their own `sub`, so clients can trust a
 * signal carrying this sender.
 */
export const CONSENT_WINDOW_SENDER_ID = 'server:consent-window'

/**
 * Push/notification `type` for the privacy-safe "call ended" alert. No longer
 * sent; installed Android builds still handle it (CallNotificationHelper).
 */
export const RECORDING_CONSENT_CALL_ENDED_PUSH_TYPE = 'RECORDING_CONSENT_CALL_ENDED'

/** Lock-screen-safe copy: no names, no call details. */
export const RECORDING_CONSENT_CALL_ENDED_NOTIFICATION = {
  title: 'Call ended',
  body: 'Call ended: recording consent needed. Tap to review.',
} as const

export type ConsentDeadlinePlan = 'none' | 'wait' | 'enforce'

/**
 * Pure deadline decision for the server's consent window. `acceptedAt` is the
 * first-ACCEPT claim time (when the call connected). Already-resolved consent,
 * an unanswered call, or a window that already fired all mean nothing to do.
 * `enforce` = mark this call not recorded (it is never ended).
 */
export function planConsentDeadline(input: {
  session:
    | (Pick<
        RecordingConsentSessionState,
        'recordingConsentAccepted' | 'recordingConsentStatus' | 'recordingConsentSkipReason'
      > & { consentTimedOutAt?: string | null })
    | null
    | undefined
  acceptedAt: string | null | undefined
  nowMs: number
}): ConsentDeadlinePlan {
  if (!input.session || !input.acceptedAt) return 'none'
  if (input.session.consentTimedOutAt) return 'none'
  if (recordingCaptureAllowedByConsent(input.session)) return 'none'
  const acceptedMs = Date.parse(input.acceptedAt)
  if (!Number.isFinite(acceptedMs)) return 'none'
  return input.nowMs < acceptedMs + CONSENT_WINDOW_MS ? 'wait' : 'enforce'
}

type ConsentPartyRow = RecordingConsentRole & RecordingPolicyConsentFields

/**
 * Before ending a call for missing consent, resolve it from durable account
 * records the way an up-to-date client would have: provider-to-provider needs
 * none, a provider callee is covered by org policy when the other party holds a
 * current acceptance, and a Member/Independent callee with a current durable
 * acceptance asserts that prior consent. Null = genuinely still owed.
 */
export function resolveConsentFromAccounts(input: {
  caller: ConsentPartyRow | null | undefined
  callee: ConsentPartyRow | null | undefined
  at?: Date
}): RecordingConsentAcceptPayload | RecordingConsentBypassedPayload | null {
  const { caller, callee } = input
  const at = input.at ?? new Date()
  if (!caller || !callee) return null
  if (isProviderRole(caller) && isProviderRole(callee)) {
    return buildRecordingConsentBypassedPayload('NOT_REQUIRED', at)
  }
  if (isProviderRole(callee)) {
    return providerPolicyCoversCounterpart(caller)
      ? buildRecordingConsentBypassedPayload('PROVIDER_POLICY', at)
      : null
  }
  if (requiresRecordingPolicyAcceptance(callee) && hasAcceptedRecordingPolicy(callee)) {
    return buildPriorRecordingConsentAcceptPayload({
      acceptedAt: callee.recordingPolicyConsentAt ?? at.toISOString(),
      noticeVersion: callee.recordingPolicyConsentNoticeVersion,
    })
  }
  return null
}

/**
 * Who the call is waiting on. Normally the callee (consent on a 1:1 call is the
 * answering party's), except a provider callee covered by org policy whose
 * Member/Independent caller holds no current acceptance.
 */
export function unconsentedPartyId(input: {
  callerId: string
  calleeId: string
  caller: ConsentPartyRow | null | undefined
  callee: ConsentPartyRow | null | undefined
}): string {
  if (
    isProviderRole(input.callee) &&
    input.caller &&
    requiresRecordingPolicyAcceptance(input.caller) &&
    !hasAcceptedRecordingPolicy(input.caller)
  ) {
    return input.callerId
  }
  return input.calleeId
}

/** First name only — never a surname or full name in this copy. */
export function firstNameForConsentNotice(input: {
  givenName?: string | null
  displayName?: string | null
}): string {
  const given = (input.givenName ?? '').trim()
  if (given) return given.split(/\s+/)[0]
  const display = (input.displayName ?? '').trim()
  if (display) return display.split(/\s+/)[0]
  return 'The other person'
}

export type ConsentCallEndedNotice = {
  callId: string
  endedAt: string
  unconsentedUserId: string
  unconsentedFirstName: string
}

export type ConsentCallEndedCopy = {
  title: string
  body: string
  /** The unconsented user is taken straight to the recording-consent prompt. */
  showConsentPrompt: boolean
}

export function consentCallEndedCopy(
  notice: Pick<ConsentCallEndedNotice, 'unconsentedUserId' | 'unconsentedFirstName'>,
  viewerUserId: string | null | undefined,
): ConsentCallEndedCopy {
  if (viewerUserId && notice.unconsentedUserId === viewerUserId) {
    return {
      title: 'Call ended',
      body: "Call ended. You haven't agreed to call recording yet, so the call was disconnected.",
      showConsentPrompt: true,
    }
  }
  const name = (notice.unconsentedFirstName ?? '').trim() || 'The other person'
  return {
    title: 'Call ended',
    body: `Call ended. ${name} hasn't agreed to call recording yet, so the call was disconnected.`,
    showConsentPrompt: false,
  }
}

/**
 * Parse a notice from a legacy END payload / profile field (older servers
 * ended the call); null when malformed.
 */
export function parseConsentCallEndedNotice(raw: unknown): ConsentCallEndedNotice | null {
  let value: unknown = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  if (typeof v.callId !== 'string' || !v.callId) return null
  if (typeof v.unconsentedUserId !== 'string' || !v.unconsentedUserId) return null
  return {
    callId: v.callId,
    endedAt: typeof v.endedAt === 'string' ? v.endedAt : new Date(0).toISOString(),
    unconsentedUserId: v.unconsentedUserId,
    unconsentedFirstName:
      typeof v.unconsentedFirstName === 'string' ? v.unconsentedFirstName : '',
  }
}

// --- Not recorded: the window lapsed, the call goes on --------------------

/** True once the consent window lapsed for this call: nothing is recorded for it. */
export function isCallNotRecordedForConsent(
  session: Pick<RecordingConsentSessionState, 'consentTimedOutAt'> | null | undefined,
): boolean {
  return Boolean(session?.consentTimedOutAt)
}

export type ConsentNotRecordedNotice = {
  callId: string
  /** When the window lapsed and the call was marked not recorded. */
  at: string
  unconsentedUserId: string
  unconsentedFirstName: string
}

/**
 * Payload of the server's not-recorded notice, a CONSENT signal from
 * CONSENT_WINDOW_SENDER_ID. It has no `reason` key, so older clients find
 * nothing in it to act on.
 */
export function buildConsentNotRecordedPayload(
  notice: Omit<ConsentNotRecordedNotice, 'callId'>,
): Record<string, string> {
  return {
    notRecordedReason: CALL_NOT_RECORDED_CONSENT_MISSING,
    at: notice.at,
    unconsentedUserId: notice.unconsentedUserId,
    unconsentedFirstName: notice.unconsentedFirstName,
  }
}

/**
 * Parse a not-recorded notice payload; null when malformed. Also reads the END
 * payload an older server stored with its timeout claim, so a call claimed just
 * before a deploy still resolves to "not recorded".
 */
export function parseConsentNotRecordedNotice(
  raw: unknown,
  callId: string,
): ConsentNotRecordedNotice | null {
  let value: unknown = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!callId || !value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  const legacyEnd = v.reason === RECORDING_CONSENT_MISSING_END_REASON
  if (v.notRecordedReason !== CALL_NOT_RECORDED_CONSENT_MISSING && !legacyEnd) return null
  if (typeof v.unconsentedUserId !== 'string' || !v.unconsentedUserId) return null
  const at =
    typeof v.at === 'string'
      ? v.at
      : typeof v.endedAt === 'string'
        ? v.endedAt
        : new Date(0).toISOString()
  return {
    callId,
    at,
    unconsentedUserId: v.unconsentedUserId,
    unconsentedFirstName:
      typeof v.unconsentedFirstName === 'string' ? v.unconsentedFirstName : '',
  }
}

export type ConsentNotRecordedCopy = {
  message: string
  /** The viewer is the one who hasn't consented (asked again on their next call). */
  isUnconsentedViewer: boolean
}

export function consentNotRecordedCopy(
  notice: Pick<ConsentNotRecordedNotice, 'unconsentedUserId' | 'unconsentedFirstName'>,
  viewerUserId: string | null | undefined,
): ConsentNotRecordedCopy {
  if (viewerUserId && notice.unconsentedUserId === viewerUserId) {
    return {
      message:
        "This call isn't being recorded because you haven't agreed to call recording. " +
        "You'll be asked again on your next call.",
      isUnconsentedViewer: true,
    }
  }
  const name = (notice.unconsentedFirstName ?? '').trim() || 'The other person'
  return {
    message: `This call isn't being recorded because ${name} hasn't agreed to call recording.`,
    isUnconsentedViewer: false,
  }
}

// --- Calls need the member's recording consent BEFORE they connect ----------
//
// Owner decision (23 Oct): a member who hasn't accepted call recording can
// neither place a call nor be rung. The server enforces it on every INVITE
// (1:1, session/telehealth, add-people room invite). Providers are never gated
// (their acknowledgement is UX only). The in-call consent sheet and the 30 s
// "continue unrecorded" window above stay as a backup for legacy clients,
// withdrawn consent and races.

export const RECORDING_CONSENT_REQUIRED_SELF = 'RECORDING_CONSENT_REQUIRED_SELF'
export const RECORDING_CONSENT_REQUIRED_PEER = 'RECORDING_CONSENT_REQUIRED_PEER'

export type RecordingConsentRequiredCode =
  | typeof RECORDING_CONSENT_REQUIRED_SELF
  | typeof RECORDING_CONSENT_REQUIRED_PEER

export type ConsentGateUser = RecordingConsentRole &
  RecordingPolicyConsentFields & { givenName?: string | null; displayName?: string | null }

export type RecordingConsentGateResult =
  | { code: typeof RECORDING_CONSENT_REQUIRED_SELF }
  | { code: typeof RECORDING_CONSENT_REQUIRED_PEER; firstName: string }

/**
 * Members/Independents (never providers) must hold a CURRENT durable
 * acceptance before a call to or from them is placed. Undecided, declined and
 * an out-of-date acceptance all count as "not accepted". The caller is checked
 * first: they must accept before anyone is rung.
 */
export function recordingConsentCallGate(
  caller: ConsentGateUser | null | undefined,
  callee: ConsentGateUser | null | undefined,
): RecordingConsentGateResult | null {
  if (caller && requiresRecordingPolicyAcceptance(caller) && !hasAcceptedRecordingPolicy(caller)) {
    return { code: RECORDING_CONSENT_REQUIRED_SELF }
  }
  if (callee && requiresRecordingPolicyAcceptance(callee) && !hasAcceptedRecordingPolicy(callee)) {
    return {
      code: RECORDING_CONSENT_REQUIRED_PEER,
      firstName: consentFirstName(callee),
    }
  }
  return null
}

/** First name only; empty when there is none (callers pick the fallback wording). */
function consentFirstName(user: { givenName?: string | null; displayName?: string | null }): string {
  const name = (user.givenName ?? '').trim() || (user.displayName ?? '').trim()
  return sanitizeConsentName(name.split(/\s+/)[0] ?? '')
}

/** Names travel inside an error message: keep them to plain name characters. */
function sanitizeConsentName(name: string): string {
  return name.replace(/[^\p{L}\p{M}'’.-]/gu, '').slice(0, 40)
}

export function recordingConsentRequiredSelfMessage(): string {
  return 'You need to accept call recording before you can make or receive calls.'
}

export function recordingConsentRequiredPeerMessage(firstName?: string | null): string {
  const name = sanitizeConsentName((firstName ?? '').trim())
  return name
    ? `${name} hasn't accepted call recording yet, so you can't call them.`
    : "They haven't accepted call recording yet, so you can't call them."
}

/**
 * The message the server rejects an INVITE with. It reads sensibly on its own
 * (legacy clients show server messages verbatim) and ends in a machine-readable
 * tag the current client parses: `(CODE)` or `(CODE:FirstName)`. The thrown
 * Error's `name` carries the code as well.
 */
export function buildRecordingConsentRequiredMessage(gate: RecordingConsentGateResult): string {
  if (gate.code === RECORDING_CONSENT_REQUIRED_SELF) {
    return `${recordingConsentRequiredSelfMessage()} (${RECORDING_CONSENT_REQUIRED_SELF})`
  }
  const name = sanitizeConsentName(gate.firstName)
  return `${recordingConsentRequiredPeerMessage(name)} (${RECORDING_CONSENT_REQUIRED_PEER}${name ? `:${name}` : ''})`
}

export type ParsedRecordingConsentRequired =
  | { code: typeof RECORDING_CONSENT_REQUIRED_SELF }
  | { code: typeof RECORDING_CONSENT_REQUIRED_PEER; firstName: string }

const CONSENT_REQUIRED_TAG =
  /\((RECORDING_CONSENT_REQUIRED_SELF|RECORDING_CONSENT_REQUIRED_PEER)(?::([^()\s]*))?\)/

/** Detects the typed rejection inside any error text (AppSync wraps messages). */
export function parseRecordingConsentRequired(
  message: string | null | undefined,
): ParsedRecordingConsentRequired | null {
  const match = CONSENT_REQUIRED_TAG.exec(message ?? '')
  if (!match) return null
  if (match[1] === RECORDING_CONSENT_REQUIRED_SELF) return { code: RECORDING_CONSENT_REQUIRED_SELF }
  return { code: RECORDING_CONSENT_REQUIRED_PEER, firstName: sanitizeConsentName(match[2] ?? '') }
}

/**
 * Editable draft for the "Send message" action — never sent automatically.
 * Easy Read: tells the person exactly how to say yes, step by step
 * (Menu → Call recording → Yes, I agree), plus a tappable link to the exact
 * screen when the caller's app knows its public URL.
 */
export function recordingConsentRequestDraft(
  firstName?: string | null,
  opts: { appUrl?: string | null } = {},
): string {
  const name = sanitizeConsentName((firstName ?? '').trim())
  const appUrl = (opts.appUrl ?? '').trim().replace(/\/$/, '')
  const lines = [
    `${name ? `Hi ${name}` : 'Hi'}. We want to call you, but calls are off until you say yes to call recording.`,
    'How to say yes:',
    '1. Tap Menu.',
    '2. Tap Call recording.',
    '3. Tap Yes, I agree.',
  ]
  if (appUrl) lines.push(`Or tap this link: ${appUrl}/?setup=recording-consent`)
  lines.push('Then we can call each other.')
  return lines.join('\n')
}

/** The question the caller sees when the callee hasn't accepted. */
export function recordingConsentPeerPrompt(firstName?: string | null): string {
  return `${recordingConsentRequiredPeerMessage(firstName)} Send them a message asking them to accept?`
}

/** Group call: people who were not called, as the notice body. */
export function recordingConsentSkippedMessage(firstNames: readonly string[]): string {
  const names = firstNames.map((n) => sanitizeConsentName(n)).filter(Boolean)
  if (names.length === 0) return "Someone wasn't called because they haven't accepted call recording yet."
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  const verb = names.length === 1 ? "wasn't" : "weren't"
  return `${list} ${verb} called because they haven't accepted call recording yet.`
}
